import React, { useRef } from 'react';
import { PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';

// Default flight cover images (same as client)
const FLIGHT_DEFAULT_IMAGES = [
  'https://assets.vinoseeker.com/site-images/kym-ellis-aF1NPSnDQLw-unsplash.jpg',
  'https://assets.vinoseeker.com/site-images/kelsey-knight-udj2tD3WKsY-unsplash.jpg',
  'https://assets.vinoseeker.com/site-images/martin-katler--nWxUpRyxs0-unsplash.jpg',
  'https://assets.vinoseeker.com/site-images/jonathan-farber-Tht21_XTkQE-unsplash.jpg',
  'https://assets.vinoseeker.com/site-images/greta-farnedi-EAt30ojfzOI-unsplash.jpg',
  'https://assets.vinoseeker.com/site-images/dan-meyers-IQVFVH0ajag-unsplash.jpg',
];

export default function FlightCoverSelector({
  coverImage,
  defaultCoverIndex,
  onSelectDefault,
  onUploadCustom,
  onRemoveCover,
  isUploading,
  canUploadCustom = true,
}) {
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onUploadCustom(file);
    }
    event.target.value = '';
  };

  const getCurrentCoverUrl = () => {
    if (coverImage?.path) {
      return `${process.env.REACT_APP_ATTACHMENT_URL}/${coverImage.path}`;
    }
    if (defaultCoverIndex !== null && defaultCoverIndex !== undefined) {
      return FLIGHT_DEFAULT_IMAGES[defaultCoverIndex];
    }
    return null;
  };

  const currentCoverUrl = getCurrentCoverUrl();
  const hasCustomImage = coverImage?.path;

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        Cover Image
      </label>

      {/* Current cover preview */}
      {currentCoverUrl && (
        <div className="relative inline-block">
          <img
            src={currentCoverUrl}
            alt="Flight cover"
            className="h-32 w-48 object-cover rounded-lg shadow"
          />
          <button
            type="button"
            onClick={onRemoveCover}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            title="Remove cover image"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Default image grid */}
      <div>
        <p className="text-sm text-gray-500 mb-2">Select a default image:</p>
        <div className="grid grid-cols-3 gap-3 max-w-md">
          {FLIGHT_DEFAULT_IMAGES.map((url, index) => (
            <button
              key={index}
              type="button"
              onClick={() => onSelectDefault(index)}
              className={`relative h-20 rounded-lg overflow-hidden border-2 transition-all ${
                defaultCoverIndex === index && !hasCustomImage
                  ? 'border-primary ring-2 ring-primary ring-offset-2'
                  : 'border-transparent hover:border-gray-300'
              }`}
            >
              <img
                src={url}
                alt={`Default cover ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Custom upload */}
      <div>
        <p className="text-sm text-gray-500 mb-2">Or upload a custom image:</p>
        {canUploadCustom ? (
          <>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <PhotoIcon className="h-5 w-5 text-gray-400" />
                    Upload Image
                  </>
                )}
              </button>
              {hasCustomImage && (
                <span className="text-sm text-green-600 font-medium">
                  Custom image uploaded
                </span>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />
          </>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-md max-w-md">
            <PhotoIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <p className="text-sm text-gray-500">
              Custom cover images are available for Premium listings and above.{' '}
              <a href="mailto:support@vinoseeker.com" className="text-primary hover:underline">
                Contact us to upgrade
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
